/**
 * AGC: AirdropManagerV3 Hook
 * AGC: Handles airdrop claims via AirdropManagerV3 contract with variable amounts
 * AGC: V3 Features:
 *   - Variable amounts based on risk score (full for verified, half for suspicious)
 *   - Amount included in EIP-712 voucher for security
 *   - Starts at round 0 for testing, round 1+ for production
 */
import { useAccount, useReadContract, useWriteContract, useWaitForTransactionReceipt, usePublicClient } from 'wagmi'
import { parseEther, formatEther } from 'viem'
import { useMiniKit } from '@coinbase/onchainkit/minikit'
import { getDeviceId } from '../utils/deviceFingerprint'
import { useFeatureFlags } from './useFeatureFlags'

// AGC: AirdropManagerV3 contract ABI
const AIRDROP_MANAGER_V3_ABI = [
  {
    name: 'currentRound',
    type: 'function',
    stateMutability: 'view',
    inputs: [],
    outputs: [{ name: '', type: 'uint256' }]
  },
  {
    name: 'tokenAirdropAmountFull',
    type: 'function',
    stateMutability: 'view',
    inputs: [],
    outputs: [{ name: '', type: 'uint256' }]
  },
  {
    name: 'tokenAirdropAmountHalf',
    type: 'function',
    stateMutability: 'view',
    inputs: [],
    outputs: [{ name: '', type: 'uint256' }]
  },
  {
    name: 'hasClaimedTokens',
    type: 'function',
    stateMutability: 'view',
    inputs: [{ name: 'wallet', type: 'address' }],
    outputs: [{ name: '', type: 'bool' }]
  },
  {
    name: 'hasClaimedNFTs',
    type: 'function',
    stateMutability: 'view',
    inputs: [{ name: 'wallet', type: 'address' }],
    outputs: [{ name: '', type: 'bool' }]
  },
  {
    name: 'claimTokenAirdrop',
    type: 'function',
    stateMutability: 'nonpayable',
    inputs: [
      { name: 'profileIdHash', type: 'bytes32' },
      { name: 'amount', type: 'uint256' },
      { name: 'nonce', type: 'uint256' },
      { name: 'expiry', type: 'uint256' },
      { name: 'signature', type: 'bytes' }
    ],
    outputs: []
  },
  {
    name: 'claimNFTAirdrop',
    type: 'function',
    stateMutability: 'nonpayable',
    inputs: [
      { name: 'profileIdHash', type: 'bytes32' },
      { name: 'nonce', type: 'uint256' },
      { name: 'expiry', type: 'uint256' },
      { name: 'signature', type: 'bytes' }
    ],
    outputs: []
  },
  {
    name: 'paused',
    type: 'function',
    stateMutability: 'view',
    inputs: [],
    outputs: [{ name: '', type: 'bool' }]
  },
  {
    name: 'getRoundStats',
    type: 'function',
    stateMutability: 'view',
    inputs: [{ name: 'round', type: 'uint256' }],
    outputs: [
      { name: 'tokenClaims', type: 'uint256' },
      { name: 'nftClaims', type: 'uint256' },
      { name: 'amountDistributed', type: 'uint256' }
    ]
  },
  // AGC: Admin functions
  {
    name: 'setTokenAirdropAmountFull',
    type: 'function',
    stateMutability: 'nonpayable',
    inputs: [{ name: '_amount', type: 'uint256' }],
    outputs: []
  },
  {
    name: 'startNewRound',
    type: 'function',
    stateMutability: 'nonpayable',
    inputs: [],
    outputs: []
  },
  {
    name: 'owner',
    type: 'function',
    stateMutability: 'view',
    inputs: [],
    outputs: [{ name: '', type: 'address' }]
  }
] as const

interface ClaimVoucherV3 {
  profileIdHash: string
  wallet: string
  amount: number        // AGC: Human-readable amount (e.g., 2000)
  amountWei: string     // AGC: Wei amount for contract (e.g., "2000000000000000000000")
  nonce: number
  expiry: number
  signature: string
  reason?: string       // AGC: Explanation (e.g., "Full airdrop eligible")
}

interface ClaimResponseV3 {
  success: boolean
  voucher: ClaimVoucherV3
  version: string
  riskScore: number
}

/**
 * AGC: Get the AirdropManagerV3 address from env
 */
export function getAirdropManagerV3Address(): `0x${string}` | undefined {
  const address = import.meta.env.VITE_AIRDROP_MANAGER_V3_ADDRESS
  if (!address || address === '0x0000000000000000000000000000000000000000') {
    return undefined
  }
  return address as `0x${string}`
}

/**
 * AGC: Hook to check if user can claim airdrop in current round
 */
export function useCanClaimAirdropV3(address?: `0x${string}`) {
  const airdropManagerV3Address = getAirdropManagerV3Address()
  const isConfigured = !!airdropManagerV3Address

  const { data: hasClaimedTokens, isLoading: tokensLoading, error, refetch: refetchTokens } = useReadContract({
    address: airdropManagerV3Address,
    abi: AIRDROP_MANAGER_V3_ABI,
    functionName: 'hasClaimedTokens',
    args: address ? [address] : undefined,
    query: {
      enabled: !!address && isConfigured,
    },
  })

  const { data: hasClaimedNFTs, isLoading: nftsLoading, refetch: refetchNFTs } = useReadContract({
    address: airdropManagerV3Address,
    abi: AIRDROP_MANAGER_V3_ABI,
    functionName: 'hasClaimedNFTs',
    args: address ? [address] : undefined,
    query: {
      enabled: !!address && isConfigured,
    },
  })

  const { data: isPaused } = useReadContract({
    address: airdropManagerV3Address,
    abi: AIRDROP_MANAGER_V3_ABI,
    functionName: 'paused',
    query: {
      enabled: isConfigured,
    },
  })

  const { data: currentRound } = useReadContract({
    address: airdropManagerV3Address,
    abi: AIRDROP_MANAGER_V3_ABI,
    functionName: 'currentRound',
    query: {
      enabled: isConfigured,
    },
  })

  const refetch = () => {
    refetchTokens()
    refetchNFTs()
  }

  // AGC: Can claim full airdrop (tokens + NFTs) if neither are claimed
  const canClaimFull = address && !hasClaimedTokens && !hasClaimedNFTs && !isPaused && isConfigured
  
  // AGC: Can claim NFTs only if tokens are claimed but NFTs are not
  const canClaimNFTsOnly = address && hasClaimedTokens && !hasClaimedNFTs && !isPaused && isConfigured

  return {
    canClaim: canClaimFull, // AGC: Full claim available
    canClaimNFTsOnly, // AGC: Only NFTs available (tokens already claimed)
    hasClaimedTokens: Boolean(hasClaimedTokens),
    hasClaimedNFTs: Boolean(hasClaimedNFTs),
    hasClaimed: Boolean(hasClaimedTokens), // AGC: Backwards compatibility
    isPaused: Boolean(isPaused),
    currentRound: currentRound ? Number(currentRound) : 0,
    isConfigured,
    isLoading: tokensLoading || nftsLoading,
    error: error as Error | null,
    refetch
  }
}

/**
 * AGC: Hook to get airdrop amount configuration
 */
export function useAirdropAmounts() {
  const airdropManagerV3Address = getAirdropManagerV3Address()
  const isConfigured = !!airdropManagerV3Address

  const { data: amountFull, refetch: refetchFull } = useReadContract({
    address: airdropManagerV3Address,
    abi: AIRDROP_MANAGER_V3_ABI,
    functionName: 'tokenAirdropAmountFull',
    query: {
      enabled: isConfigured,
    },
  })

  const { data: amountHalf, refetch: refetchHalf } = useReadContract({
    address: airdropManagerV3Address,
    abi: AIRDROP_MANAGER_V3_ABI,
    functionName: 'tokenAirdropAmountHalf',
    query: {
      enabled: isConfigured,
    },
  })

  const refetch = () => {
    refetchFull()
    refetchHalf()
  }

  return {
    fullAmount: amountFull ? BigInt(amountFull) : undefined,
    halfAmount: amountHalf ? BigInt(amountHalf) : undefined,
    fullAmountFormatted: amountFull ? formatEther(amountFull) : '0',
    halfAmountFormatted: amountHalf ? formatEther(amountHalf) : '0',
    isConfigured,
    refetch
  }
}

/**
 * AGC: Hook for claiming token airdrop via AirdropManagerV3
 */
export function useClaimAirdropV3() {
  const { address, isConnected } = useAccount()
  // AGC: Conditionally use MiniKit only in Base App context (v5.0.1 pattern)
  const { isBaseAppContext } = useFeatureFlags()
  const miniKit = isBaseAppContext ? useMiniKit() : { context: null }
  const { context } = miniKit
  const { writeContractAsync, data: hash, isPending, error } = useWriteContract()
  const publicClient = usePublicClient()
  
  // AGC: Wait for transaction receipt
  const { data: receipt, isLoading: isConfirming } = useWaitForTransactionReceipt({
    hash,
  })

  const fid = context?.user?.fid
  const backendUrl = import.meta.env.VITE_BACKEND_URL || 'http://localhost:3001'
  const airdropManagerV3Address = getAirdropManagerV3Address()

  /**
   * AGC: Claim token airdrop via AirdropManagerV3 with backend voucher
   * AGC: Amount is determined by backend based on risk score:
   *   - Score < 30: full amount (e.g., 2000 OGB)
   *   - Score 30-59: half amount (e.g., 1000 OGB)
   *   - Score >= 60: 0 (rejected)
   */
  const claimTokenAirdrop = async (): Promise<{ txHash: string; amount: number; reason?: string }> => {
    if (!fid) {
      throw new Error('Base FID not found. Please open in Base App.')
    }

    if (!address) {
      throw new Error('Wallet not connected')
    }

    if (!airdropManagerV3Address) {
      throw new Error('AirdropManagerV3 contract address not configured')
    }

    // AGC: Get device fingerprint
    const deviceId = getDeviceId()

    // AGC: Request signed voucher from backend (V3 includes amount based on risk score)
    const response = await fetch(`${backendUrl}/api/airdrop/claim`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-FID': String(fid),
      },
      body: JSON.stringify({
        profileId: fid,
        walletAddress: address,
        deviceId,
        version: 'v3' // AGC: Request V3 voucher with amount
      }),
    })

    if (!response.ok) {
      let errorData
      try {
        errorData = await response.json()
      } catch {
        errorData = { error: `HTTP ${response.status}: ${response.statusText}` }
      }
      const errorMessage = errorData.error || errorData.reason || `Failed to claim airdrop (${response.status})`
      console.error('🎁 [AirdropV3] Backend error:', { status: response.status, errorData })
      throw new Error(errorMessage)
    }

    const data: ClaimResponseV3 = await response.json()
    const { voucher, riskScore } = data

    console.log(`🎁 [AirdropV3] Voucher received: ${voucher.amount} OGB (risk score: ${riskScore}, reason: ${voucher.reason})`)

    // AGC: Call AirdropManagerV3 contract with signed voucher (includes amount)
    console.log('🎁 [AirdropV3] Calling AirdropManagerV3.claimTokenAirdrop()...')
    
    const contractConfig = {
      address: airdropManagerV3Address,
      abi: AIRDROP_MANAGER_V3_ABI,
      functionName: 'claimTokenAirdrop' as const,
      args: [
        voucher.profileIdHash as `0x${string}`,
        BigInt(voucher.amountWei), // AGC: Amount in wei from backend
        BigInt(voucher.nonce),
        BigInt(voucher.expiry),
        voucher.signature as `0x${string}`,
      ],
    }

    try {
      // AGC: Try direct call first
      const txHash = await writeContractAsync(contractConfig)
      console.log('🎁 [AirdropV3] Airdrop claim transaction submitted:', txHash)
      return { txHash, amount: voucher.amount, reason: voucher.reason }
    } catch (error: any) {
      console.warn('⚠️ [AirdropV3] Direct token claim failed, attempting gas estimation fallback...', error)
      
      // AGC: If wallet-side estimation failed, try manual gas estimation
      if (!publicClient || !address) {
        // AGC: Extract better error message
        const errorMessage = error?.shortMessage || error?.message || error?.reason || 'Transaction failed'
        throw new Error(`Token claim failed: ${errorMessage}`)
      }

      try {
        // AGC: Estimate gas manually to get better error messages
        console.log('🔍 [AirdropV3] Estimating gas manually...')
        const gasEstimate = await publicClient.estimateContractGas({
          ...contractConfig,
          account: address
        })
        console.log('✅ [AirdropV3] Gas estimate:', gasEstimate.toString())

        // AGC: Get fee data
        let feeOverrides: {
          gas?: bigint
          maxFeePerGas?: bigint
          maxPriorityFeePerGas?: bigint
          gasPrice?: bigint
        } = { gas: gasEstimate }

        try {
          // AGC: Prefer EIP-1559 fee data when available
          const feeData = await publicClient.estimateFeesPerGas()
          feeOverrides = {
            gas: gasEstimate,
            maxFeePerGas: feeData.maxFeePerGas,
            maxPriorityFeePerGas: feeData.maxPriorityFeePerGas
          }
          console.log('✅ [AirdropV3] Fee data estimated:', feeOverrides)
        } catch (feeError) {
          console.warn('⚠️ [AirdropV3] Failed to estimate EIP-1559 fees, using gasPrice', feeError)
          try {
            const gasPrice = await publicClient.getGasPrice()
            feeOverrides = {
              gas: gasEstimate,
              gasPrice
            }
          } catch (gasPriceError) {
            console.warn('⚠️ [AirdropV3] Unable to fetch gasPrice fallback:', gasPriceError)
          }
        }

        // AGC: Retry with manual gas estimation
        const txHash = await writeContractAsync({
          ...contractConfig,
          ...feeOverrides
        })
        console.log('✅ [AirdropV3] Token airdrop claim transaction submitted (with manual gas):', txHash)
        return { txHash, amount: voucher.amount, reason: voucher.reason }
      } catch (gasError: any) {
        // AGC: Gas estimation failed - this means the transaction would revert
        const revertReason = gasError?.shortMessage || gasError?.message || gasError?.reason || 'Transaction would revert'
        console.error('❌ [AirdropV3] Gas estimation failed:', revertReason)
        
        // AGC: Check for common revert reasons
        if (revertReason.includes('already claimed') || revertReason.includes('Already claimed')) {
          throw new Error('Tokens have already been claimed for this round')
        } else if (revertReason.includes('paused') || revertReason.includes('Paused')) {
          throw new Error('Airdrop is currently paused')
        } else if (revertReason.includes('expired') || revertReason.includes('Expired')) {
          throw new Error('Airdrop voucher has expired. Please try claiming again.')
        } else if (revertReason.includes('invalid signature') || revertReason.includes('Invalid signature')) {
          throw new Error('Invalid airdrop signature. Please try claiming again.')
        } else {
          throw new Error(`Token claim failed: ${revertReason}`)
        }
      }
    }
  }

  /**
   * AGC: Claim NFT airdrop via AirdropManagerV3 with backend voucher
   */
  const claimNFTAirdrop = async () => {
    if (!fid) {
      throw new Error('Base FID not found. Please open in Base App.')
    }

    if (!address) {
      throw new Error('Wallet not connected')
    }

    if (!airdropManagerV3Address) {
      throw new Error('AirdropManagerV3 contract address not configured')
    }

    // AGC: Get device fingerprint
    const deviceId = getDeviceId()

    // AGC: Request signed voucher from backend for NFT claim
    const response = await fetch(`${backendUrl}/api/airdrop/claim-nft`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-FID': String(fid),
      },
      body: JSON.stringify({
        profileId: fid,
        walletAddress: address,
        deviceId,
        version: 'v3' // AGC: Request V3 voucher
      }),
    })

    if (!response.ok) {
      let errorData
      try {
        errorData = await response.json()
      } catch {
        errorData = { error: `HTTP ${response.status}: ${response.statusText}` }
      }
      const errorMessage = errorData.error || errorData.reason || `Failed to claim NFT airdrop (${response.status})`
      console.error('🎁 [AirdropV3] NFT backend error:', errorData)
      throw new Error(errorMessage)
    }

    const data = await response.json()
    const { voucher } = data

    // AGC: Call AirdropManagerV3 contract with signed voucher
    console.log('🎁 [AirdropV3] Calling AirdropManagerV3.claimNFTAirdrop()...')
    
    const contractConfig = {
      address: airdropManagerV3Address,
      abi: AIRDROP_MANAGER_V3_ABI,
      functionName: 'claimNFTAirdrop' as const,
      args: [
        voucher.profileIdHash as `0x${string}`,
        BigInt(voucher.nonce),
        BigInt(voucher.expiry),
        voucher.signature as `0x${string}`,
      ],
    }

    try {
      // AGC: Try direct call first
      const txHash = await writeContractAsync(contractConfig)
      console.log('🎁 [AirdropV3] NFT airdrop claim transaction submitted:', txHash)
      return txHash
    } catch (error: any) {
      console.warn('⚠️ [AirdropV3] Direct NFT claim failed, attempting gas estimation fallback...', error)
      
      // AGC: If wallet-side estimation failed, try manual gas estimation
      if (!publicClient || !address) {
        // AGC: Extract better error message
        const errorMessage = error?.shortMessage || error?.message || error?.reason || 'Transaction failed'
        throw new Error(`NFT claim failed: ${errorMessage}`)
      }

      try {
        // AGC: Estimate gas manually to get better error messages
        console.log('🔍 [AirdropV3] Estimating gas manually...')
        const gasEstimate = await publicClient.estimateContractGas({
          ...contractConfig,
          account: address
        })
        console.log('✅ [AirdropV3] Gas estimate:', gasEstimate.toString())

        // AGC: Get fee data
        let feeOverrides: {
          gas?: bigint
          maxFeePerGas?: bigint
          maxPriorityFeePerGas?: bigint
          gasPrice?: bigint
        } = { gas: gasEstimate }

        try {
          // AGC: Prefer EIP-1559 fee data when available
          const feeData = await publicClient.estimateFeesPerGas()
          feeOverrides = {
            gas: gasEstimate,
            maxFeePerGas: feeData.maxFeePerGas,
            maxPriorityFeePerGas: feeData.maxPriorityFeePerGas
          }
          console.log('✅ [AirdropV3] Fee data estimated:', feeOverrides)
        } catch (feeError) {
          console.warn('⚠️ [AirdropV3] Failed to estimate EIP-1559 fees, using gasPrice', feeError)
          try {
            const gasPrice = await publicClient.getGasPrice()
            feeOverrides = {
              gas: gasEstimate,
              gasPrice
            }
          } catch (gasPriceError) {
            console.warn('⚠️ [AirdropV3] Unable to fetch gasPrice fallback:', gasPriceError)
          }
        }

        // AGC: Retry with manual gas estimation
        const txHash = await writeContractAsync({
          ...contractConfig,
          ...feeOverrides
        })
        console.log('✅ [AirdropV3] NFT airdrop claim transaction submitted (with manual gas):', txHash)
        return txHash
      } catch (gasError: any) {
        // AGC: Gas estimation failed - this means the transaction would revert
        const revertReason = gasError?.shortMessage || gasError?.message || gasError?.reason || 'Transaction would revert'
        console.error('❌ [AirdropV3] Gas estimation failed:', revertReason)
        
        // AGC: Check for common revert reasons
        if (revertReason.includes('already claimed') || revertReason.includes('Already claimed')) {
          throw new Error('NFTs have already been claimed for this round')
        } else if (revertReason.includes('paused') || revertReason.includes('Paused')) {
          throw new Error('Airdrop is currently paused')
        } else if (revertReason.includes('expired') || revertReason.includes('Expired')) {
          throw new Error('Airdrop voucher has expired. Please try claiming again.')
        } else if (revertReason.includes('invalid signature') || revertReason.includes('Invalid signature')) {
          throw new Error('Invalid airdrop signature. Please try claiming again.')
        } else {
          throw new Error(`NFT claim failed: ${revertReason}`)
        }
      }
    }
  }

  /**
   * AGC: Claim both token and NFT airdrops (two separate transactions)
   * AGC: Smart handling - if tokens already claimed, skip to NFTs
   */
  const claimFullAirdrop = async () => {
    let tokenResult: { txHash: string; amount: number; reason?: string } | null = null
    let tokenSkipped = false
    
    // AGC: First try to claim tokens (will fail if already claimed)
    try {
      console.log('🎁 [AirdropV3] Attempting to claim tokens...')
      tokenResult = await claimTokenAirdrop()
      console.log(`🎁 [AirdropV3] Token airdrop claimed: ${tokenResult.amount} OGB`)
      
      // AGC: Wait for token claim to be confirmed before NFT claim
      await new Promise(resolve => setTimeout(resolve, 3000))
    } catch (tokenError: any) {
      // AGC: Check if error indicates tokens already claimed
      const errorMessage = tokenError?.message?.toLowerCase() || ''
      const alreadyClaimed = errorMessage.includes('already claimed') || 
                             errorMessage.includes('wallet already claimed') ||
                             errorMessage.includes('tokens already claimed')
      
      if (alreadyClaimed) {
        console.log('🎁 [AirdropV3] Tokens already claimed, proceeding to NFTs...')
        tokenSkipped = true
      } else {
        // AGC: Rethrow other errors
        throw tokenError
      }
    }
    
    // AGC: Now claim NFTs (separate transaction)
    let nftTxHash: string
    try {
      console.log('🎁 [AirdropV3] Claiming NFTs...')
      nftTxHash = await claimNFTAirdrop()
      console.log('🎁 [AirdropV3] NFT airdrop claimed')
    } catch (nftError: any) {
      // AGC: Check if NFTs already claimed
      const errorMessage = nftError?.message?.toLowerCase() || ''
      const alreadyClaimed = errorMessage.includes('already claimed') || 
                             errorMessage.includes('wallet already claimed') ||
                             errorMessage.includes('nfts already claimed')
      
      if (alreadyClaimed) {
        console.log('🎁 [AirdropV3] NFTs already claimed')
        // AGC: If both were already claimed, throw appropriate error
        if (tokenSkipped) {
          throw new Error('Both tokens and NFTs have already been claimed for this round!')
        }
        // AGC: Tokens were claimed, NFTs were already done - partial success
        return {
          tokenTxHash: tokenResult?.txHash || '',
          tokenAmount: tokenResult?.amount || 0,
          tokenReason: tokenResult?.reason,
          nftTxHash: '',
          nftSkipped: true
        }
      }
      throw nftError
    }
    
    return { 
      tokenTxHash: tokenResult?.txHash || '',
      tokenAmount: tokenResult?.amount || 0,
      tokenReason: tokenResult?.reason,
      tokenSkipped,
      nftTxHash 
    }
  }

  /**
   * AGC: Claim NFTs only (for users who already claimed tokens but missed NFTs)
   */
  const claimNFTsOnly = async () => {
    console.log('🎁 [AirdropV3] Claiming NFTs only...')
    const nftTxHash = await claimNFTAirdrop()
    console.log('🎁 [AirdropV3] NFT airdrop claimed')
    return { nftTxHash }
  }

  return {
    claimTokenAirdrop,
    claimNFTAirdrop,
    claimNFTsOnly, // AGC: Convenience function for users who missed NFTs
    claimFullAirdrop,
    hash,
    receipt,
    isPending,
    isConfirming,
    isSuccess: receipt?.status === 'success',
    error,
    canClaim: !!fid && isConnected && !!airdropManagerV3Address,
  }
}

/**
 * AGC: Hook for admin functions
 */
export function useAirdropAdminV3() {
  const { address } = useAccount()
  const airdropManagerV3Address = getAirdropManagerV3Address()
  const { writeContractAsync, isPending, error } = useWriteContract()

  const { data: owner } = useReadContract({
    address: airdropManagerV3Address,
    abi: AIRDROP_MANAGER_V3_ABI,
    functionName: 'owner',
    query: {
      enabled: !!airdropManagerV3Address,
    },
  })

  const isOwner = owner && address && owner.toLowerCase() === address.toLowerCase()

  /**
   * AGC: Set full airdrop amount (half is calculated automatically)
   */
  const setFullAmount = async (amountInTokens: number) => {
    if (!airdropManagerV3Address) throw new Error('Contract not configured')
    if (!isOwner) throw new Error('Only owner can call this function')

    const amountInWei = parseEther(amountInTokens.toString())
    
    return writeContractAsync({
      address: airdropManagerV3Address,
      abi: AIRDROP_MANAGER_V3_ABI,
      functionName: 'setTokenAirdropAmountFull',
      args: [amountInWei],
    })
  }

  /**
   * AGC: Start new airdrop round
   */
  const startNewRound = async () => {
    if (!airdropManagerV3Address) throw new Error('Contract not configured')
    if (!isOwner) throw new Error('Only owner can call this function')

    return writeContractAsync({
      address: airdropManagerV3Address,
      abi: AIRDROP_MANAGER_V3_ABI,
      functionName: 'startNewRound',
      args: [],
    })
  }

  return {
    setFullAmount,
    startNewRound,
    isOwner,
    isPending,
    error,
  }
}

export { AIRDROP_MANAGER_V3_ABI }

